import { DualProgressBar, Trans } from '@/components'
import { formatStorageGB } from '@/utils/storage'

type StorageColorVariant = 'tertiary' | 'secondary' | 'primary'

const COLOR_CLASSES: Record<StorageColorVariant, string> = {
  tertiary: 'bg-btn-tertiary',
  secondary: 'bg-btn-secondary',
  primary: 'bg-btn-primary',
}

interface StorageCardProps {
  title: React.ReactNode
  usage: number
  quota: number
  colorVariant?: StorageColorVariant
  legendText: React.ReactNode
  actions?: React.ReactNode
  snapshotCount?: number
  snapshotQuota?: number
}

/**
 * Shared storage overview card component
 * Displays storage usage, quota, and optional snapshot slot information
 */
export function StorageCard({
  title,
  usage,
  quota,
  colorVariant = 'tertiary',
  legendText,
  actions,
  snapshotCount,
  snapshotQuota,
}: StorageCardProps) {
  const usedColor = COLOR_CLASSES[colorVariant]

  return (
    <div className="bg-panel-white border border-neutral-100 p-6 rounded-[32px]">
      <div className="flex items-center justify-between mb-4 t-sb">
        <div>{title}</div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
      <DualProgressBar usage={usage} quota={quota} usedColor={usedColor} />
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1 t-sb text-secondary">
          <span className="text-primary">{formatStorageGB(usage)}</span>{' '}
          <Trans i18nKey="space.of">of</Trans>{' '}
          <span className="text-primary">{formatStorageGB(quota, { decimals: 0 })}</span>
          {snapshotCount !== undefined && snapshotQuota !== undefined && (
            <>
              <span className="mx-2 h-3 w-0.5 bg-neutral-200"></span>
              <span className="text-primary">{snapshotCount}</span>
              <span className="text-secondary">
                <Trans i18nKey="space.of">of</Trans>
              </span>
              <span className="text-primary">{snapshotQuota}</span>
              <span>
                <Trans i18nKey="snapshot.slot">Slots</Trans>
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-sm ${usedColor}`} />
          <span className="t-sb text-secondary">{legendText}</span>
        </div>
      </div>
    </div>
  )
}
